/**
 * What a `sales` user may OPEN in the office app — the frontend half of the
 * API's deny-by-default guard (fieldiq-api src/middleware/sales-role-guard.ts,
 * api#1032).
 *
 * The operator's rule (2026-10-11): a sales user READS customers, contacts,
 * quotes, jobs and tickets, and files and reads their OWN expenses. Nothing
 * else — no pay data, no other people's timesheets or expenses, no approvals,
 * no settings. The API refuses a sales token on every route not on its
 * allowlist; this file stops the app from drawing a way into a screen whose
 * every call would answer 403.
 *
 * ── DENY BY DEFAULT, BY THE ROUTE THE ROUTER WOULD PICK ─────────────────────
 * A path is open to sales only if react-router would dispatch it to one of
 * SALES_ROUTES. `matchRoutes` ranks static segments over params exactly as the
 * app's own <Routes> does, so `/jobs/new` resolves to the `/jobs/new` sibling
 * (closed), not to `/jobs/:jobId` (open). The static siblings are listed in
 * CLOSED_SIBLINGS for that ranking alone; a path matching neither list is
 * closed too. `sales-access.test.ts` walks every `path="…"` in App.tsx and
 * fails if a route outside SALES_ROUTES would open — so a sibling added to
 * App.tsx later is closed to sales until someone says otherwise.
 *
 * ── THIS HIDES; IT DOES NOT ENFORCE ─────────────────────────────────────────
 * The API is the enforcement. A screen that is open here still has controls a
 * sales user may not use (the API answers 403 on the write), and the CRM routes
 * of CRM-API-2..4 must be added BOTH to the API allowlist and to SALES_ROUTES.
 *
 * Every other role is untouched by the sales list: `routeOpenForRole` returns
 * true for any role that is not `sales`. The one rule that reaches other roles
 * is the CRM rule (`isCrmPath`): /sales pages are drawn only for CRM_ROLES.
 */
import { matchRoutes } from 'react-router-dom';
import { isCrmRole } from './roles';

/** The office-app routes a sales user may open. Patterns, as written in App.tsx. */
export const SALES_ROUTES = [
  '/customers',
  '/customers/:customerId',
  '/quotes',
  '/quotes/:quoteId',
  '/jobs',
  '/jobs/:jobId',
  '/tickets',
  '/tickets/:ticketId',
  '/expenses',
  '/expenses/new',
  // The CRM (CRM-FE-2..4): every route under /sales is also CRM_ROLES-only for
  // every role — see isCrmPath below.
  '/sales/meetings',
  '/sales/meetings/new',
  '/sales/meetings/:meetingId',
  '/sales/meetings/:meetingId/edit',
  '/sales/pipeline',
  '/sales/pipeline/:opportunityId',
  // The phone's nav hub. Its entries are filtered by the same rule.
  '/more',
  // The assistant (api#1076 opened chat, voice, TTS and own conversations to
  // sales; Scout's tools are CRM_TOOL_ROLES). /ai-agent/context and the agent
  // profile stay closed, so the screen asks for neither as sales.
  '/agent',
] as const;

/**
 * Static siblings of a SALES_ROUTES param pattern. Listed so `matchRoutes` ranks
 * them over the param exactly as the app does; every one is CLOSED to sales.
 */
const CLOSED_SIBLINGS = ['/jobs/new', '/jobs/trash', '/quotes/create', '/tickets/new'] as const;

/** Where a sales user lands — the customer list is in every vertical. */
export const SALES_HOME = '/customers';

const ROUTE_TABLE = [
  ...SALES_ROUTES.map((path) => ({ path, open: true })),
  ...CLOSED_SIBLINGS.map((path) => ({ path, open: false })),
];

/** Would the router dispatch this concrete pathname to a route open to sales? */
export function salesMayOpen(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || '/';
  const matches = matchRoutes(ROUTE_TABLE, path);
  if (!matches || matches.length === 0) return false;
  return (matches[matches.length - 1].route as { open: boolean }).open;
}

export function isSalesRole(role: string | undefined | null): boolean {
  return role === 'sales';
}

/** May this role open this route? Every role but `sales` may open everything it could before. */
export function routeOpenForRole(role: string | undefined | null, pathname: string): boolean {
  return !isSalesRole(role) || salesMayOpen(pathname);
}

/**
 * THE CRM PAGES ARE CRM_ROLES ONLY, FOR EVERY ROLE (sales-log D5/D12). Every
 * /sales route reads /sales-activities or /opportunities, which the API gates
 * with CRM_ROLES (admin, office_staff, supervisor, dispatcher, sales) — so a
 * `logistics` user, who has the office app, would open a page whose every call
 * answers 403. The nav does not draw it for them; `CrmRouteGate` renders the
 * refusal if they type the URL.
 */
export function isCrmPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0];
  return path === '/sales' || path.startsWith('/sales/');
}

/** Should a nav entry pointing at `path` be drawn for this role? Same rule as the route, plus the CRM rule. */
export function navPathOpenForRole(role: string | undefined | null, path: string): boolean {
  if (isCrmPath(path) && !isCrmRole(role)) return false;
  return routeOpenForRole(role, path);
}
